import React from 'react';
import { MilitaryHistoryItemModel } from 'forms/sections/military/history/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import DateRange from 'components/Form/DateRange';
import Show from 'components/Form/Show';
import Branch from 'components/Form/Branch';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import { setAttribute } from 'components/Form/shared/setAttribute';
import { LocationModel } from 'forms/shared/types';
import Layouts from 'components/Form/PVQLocation/Layouts';
import PVQLocation from 'components/Form/PVQLocation';
import DisciplinaryCollection from '../DisciplinaryCollection';

interface Props extends ValidationElementProps, Partial<MilitaryHistoryItemModel> {
  bind?: boolean;
  formType?: string;
  name?: string;
  onUpdate?: (militaryHistoryItemModel: Partial<MilitaryHistoryItemModel>) => void;
  onError?: CommonOnError
  className?: string
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
}

export default class MilitaryService extends ValidationElement<Props> {
  static defaultProps: Props;

  update = (queue: Partial<MilitaryHistoryItemModel>) => {
    this.props.onUpdate?.({
      List: this.props.List,
      Rank: this.props.Rank,
      Service: this.props.Service,
      Status: this.props.Status,
      StatusOtherExplanation: this.props.StatusOtherExplanation,
      Officer: this.props.Officer,
      ServiceNumber: this.props.ServiceNumber,
      Dates: this.props.Dates,
      ServiceState: this.props.ServiceState,
      HasBeenDischarged: this.props.HasBeenDischarged,
      DischargeType: this.props.DischargeType,
      DischargeTypeOther: this.props.DischargeTypeOther,
      DischargeReason: this.props.DischargeReason,
      DischargeDate: this.props.DischargeDate,
      Comments: this.props.Comments,
      DischargeComments: this.props.DischargeComments,
      OtherThanHonorableComments: this.props.OtherThanHonorableComments,
      Country: this.props.Country,
      CountryFocused: this.props.CountryFocused,
      ...queue,
    });
  };

  updateList = (value: MilitaryHistoryItemModel['List']) => {
    this.update({
      List: value,
    });
  };

  updateRank = (value: MilitaryHistoryItemModel['Rank']) => {
    this.update({
      Rank: value,
    });
  };

  updateService = (values: MilitaryHistoryItemModel['Service']) => {
    this.update({
      Service: values,
      ServiceState: ['AirNationalGuard', 'ArmyNationalGuard'].includes(values.value)
        ? this.props.ServiceState
        : {} as LocationModel,
    });
  };

  updateStatus = (status: MilitaryHistoryItemModel['Status']) => {
    const queue: Partial<MilitaryHistoryItemModel> = { Status: status };

    if (this.props.StatusOtherExplanation?.value && status.value !== 'Other') {
      queue.StatusOtherExplanation = {} as MilitaryHistoryItemModel['StatusOtherExplanation'];
    }

    this.update(queue);
  };

  updateStatusOtherExplanation = (values: MilitaryHistoryItemModel['StatusOtherExplanation']) => {
    this.update({
      StatusOtherExplanation: values,
    });
  };

  updateOfficer = (values: MilitaryHistoryItemModel['Officer']) => {
    this.update({
      Officer: values,
    });
  };

  updateServiceNumber = (value: MilitaryHistoryItemModel['ServiceNumber']) => {
    this.update({
      ServiceNumber: value,
    });
  };

  updateDates = (dates: MilitaryHistoryItemModel['Dates']) => {
    this.update({
      Dates: dates,
    });
  };

  updateServiceState = (value: MilitaryHistoryItemModel['ServiceState']) => {
    this.update({
      ServiceState: value,
    });
  };

  updateDischarged = (values: MilitaryHistoryItemModel['HasBeenDischarged']) => {
    // If there is no history clear out any previously entered data
    this.update({
      HasBeenDischarged: values,
      DischargeType: values.value !== 'Yes' ? null : this.props.DischargeType,
      DischargeReason: values.value !== 'Yes' ? null : this.props.DischargeReason,
      DischargeDate: values.value !== 'Yes' ? null : this.props.DischargeDate,
    });
  };

  updateDischargeType = (dischargeType: MilitaryHistoryItemModel['DischargeType']) => {
    const queue: Partial<MilitaryHistoryItemModel> = { DischargeType: dischargeType };

    if (this.props.DischargeReason?.value && dischargeType.value !== 'OtherType') {
      queue.DischargeReason = {} as MilitaryHistoryItemModel['DischargeReason'];
    }

    this.update(queue);
  };

  updateDischargeTypeOther = (value: MilitaryHistoryItemModel['DischargeTypeOther']) => {
    this.update({
      DischargeTypeOther: value,
    });
  };

  updateDischargeReason = (value: MilitaryHistoryItemModel['DischargeReason']) => {
    this.update({
      DischargeReason: value,
    });
  };

  updateDischargeDate = (value: MilitaryHistoryItemModel['DischargeDate']) => {
    this.update({
      DischargeDate: value,
    });
  };

  updateComments = (value: MilitaryHistoryItemModel['Comments']) => {
    this.update({
      Comments: value,
    });
  };

  updateDischargeComments = (value: MilitaryHistoryItemModel['DischargeComments']) => {
    this.update({
      DischargeComments: value,
    });
  };

  updateOtherThanHonorableComments = (value: MilitaryHistoryItemModel['OtherThanHonorableComments']) => {
    this.update({
      OtherThanHonorableComments: value,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div className="military-service">
        {/* PVQ: When did you serve? (Start with your most recent period of service and work back.) */}
        <Field
          title={i18n.t('military.pvq.history.heading.dates')}
          help="military.history.help.dates"
          adjustFor="daterange"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={setAttribute(attribute, 'Dates')}
        >
          <DateRange
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.props.Dates}
            attribute={setAttribute(attribute, 'Dates')}
            className="dates"
            fromDateLabel={i18n.t('military.pvq.history.label.dates.fromdate')}
            minDateEqualTo
            name="Dates"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            onUpdate={this.updateDates}
            required={this.props.required}
            toDateLabel={i18n.t('military.pvq.history.label.dates.todate')}
            usePvqFromDate
            usePvqToDate
          />
        </Field>
        {/* PVQ: Which branch of service are you or were you in? */}
        <Field
          adjustFor="big-buttons"
          attribute={setAttribute(attribute, 'Service')}
          exactAttribute
          scrollIntoView={this.props.scrollIntoView}
          shrink
          title={i18n.t('military.pvq.history.heading.service')}
        >
          <RadioGroup
            attribute={setAttribute(attribute, 'Service')}
            className="service option-list eapp-extend-labels option-list-vertical"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            required={this.props.required}
            selectedValue={this.props.Service?.value}
          >
            <Radio
              className="service-airnationalguard"
              label={i18n.t('military.pvq.history.label.airnationalguard')}
              name="service-airnationalguard"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="AirNationalGuard"
            />
            <Radio
              className="service-armynationalguard"
              label={i18n.t('military.pvq.history.label.armynationalguard')}
              name="service-armynationalguard"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="ArmyNationalGuard"
            />
            <Radio
              className="service-nationaloceanicatmosphericadministration"
              label={i18n.t('military.pvq.history.label.nationaloceanicatmosphericadministration')}
              name="service-nationaloceanicatmosphericadministration"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="NationalOceanicAtmosphericAdministration"
            />
            <Radio
              className="service-airforce"
              label={i18n.t('military.pvq.history.label.airforce')}
              name="service-airforce"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="AirForce"
            />
            <Radio
              className="service-army"
              label={i18n.t('military.pvq.history.label.army')}
              name="service-army"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="Army"
            />
            <Radio
              className="service-coastguard"
              label={i18n.t('military.pvq.history.label.coastguard')}
              name="service-coastguard"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="CoastGuard"
            />
            <Radio
              className="service-marinecorps"
              label={i18n.t('military.pvq.history.label.marinecorps')}
              name="service-marinecorps"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="MarineCorps"
            />
            <Radio
              className="service-navy"
              label={i18n.t('military.pvq.history.label.navy')}
              name="service-navy"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="Navy"
            />
            <Radio
              className="service-publichealthservice"
              label={i18n.t('military.pvq.history.label.publichealthservice')}
              name="service-publichealthservice"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="PublicHealthService"
            />
            <Radio
              className="service-spaceforce"
              label={i18n.t('military.pvq.history.label.spaceforce')}
              name="service-spaceforce"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateService}
              value="SpaceForce"
            />
          </RadioGroup>
        </Field>
        {(this.props.Service?.value === 'AirNationalGuard' || this.props.Service?.value === 'ArmyNationalGuard') && (
          <>
            {/* PVQ: Which state or territory do you or did you serve in the National Guard? */}
            <Field
              adjustFor="labels"
              attribute={setAttribute(attribute, 'ServiceState')}
              scrollIntoView={this.props.scrollIntoView}
            >
              <PVQLocation
              // eslint-disable-next-line react/jsx-props-no-spreading
                {...this.props.ServiceState}
                attribute={setAttribute(attribute, 'ServiceState')}
                className="service-state"
                layout={Layouts.STATE}
                name="ServiceState"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updateServiceState}
                required={this.props.required}
                stateLabel={i18n.t('military.pvq.history.heading.servicestate')}
              />
            </Field>
          </>
        )}
        {/* PVQ: Rank (List the alpha-numeric identifier for highest rank achieved [...]) */}
        <Field
          adjustFor="labels"
          attribute={setAttribute(attribute, 'Rank')}
          scrollIntoView={this.props.scrollIntoView}
          title={i18n.t('military.pvq.history.heading.rank')}
        >
          <Text
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.props.Rank}
            attribute={setAttribute(attribute, 'Rank')}
            className="military-service-rank"
            maxlength="10"
            name="rank"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            onUpdate={this.updateRank}
            prefix="rank"
            required={this.props.required}
          />
        </Field>
        {/* PVQ: What is or was your duty status? */}
        <Field
          adjustFor="buttons"
          attribute={setAttribute(attribute, 'Status')}
          scrollIntoView={this.props.scrollIntoView}
          shrink
          title={i18n.t('military.pvq.history.heading.status')}
        >
          <RadioGroup
            attribute={setAttribute(attribute, 'Status')}
            className="status option-list option-list-vertical"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            required={this.props.required}
            selectedValue={(this.props.Status || {}).value}
          >
            <Radio
              className="status-activeduty"
              label={i18n.t('military.pvq.history.label.activeduty')}
              name="status-activeduty"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateStatus}
              value="ActiveDuty"
            />
            <Radio
              className="status-activereserve"
              label={i18n.t('military.pvq.history.label.activereserve')}
              name="status-activereserve"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateStatus}
              value="ActiveReserve"
            />
            <Radio
              className="status-traditionalreservist"
              label={i18n.t('military.pvq.history.label.traditionalreservist')}
              name="status-traditionalreservist"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateStatus}
              value="TraditionalReservist"
            />
            <Radio
              className="status-inactivereserve"
              label={i18n.t('military.pvq.history.label.inactivereserve')}
              name="status-inactivereserve"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateStatus}
              value="InactiveReserve"
            />
            <Radio
              className="status-retiredreserve"
              label={i18n.t('military.pvq.history.label.retiredreserve')}
              name="status-retiredreserve"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateStatus}
              value="RetiredReserve"
            />
            <Radio
              className="status-other"
              label={i18n.t('military.pvq.history.label.other')}
              name="status-other"
              onUpdate={this.updateStatus}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              value="Other"
            />
          </RadioGroup>
          {this.props.Status?.value === 'Other' && (
            <>
              {/* PVQ: Please explain. */}
              <Field
                adjustFor="labels"
                attribute={setAttribute(attribute, 'StatusOtherExplanation')}
                scrollIntoView={this.props.scrollIntoView}
                title={i18n.t('military.pvq.history.label.statusotherexplanation')}
              >
                <Textarea
                  {...this.props.StatusOtherExplanation}
                  attribute={setAttribute(attribute, 'StatusOtherExplanation')}
                  className="military-service-status-other-explanation"
                  name="statusOtherExplanation"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateStatusOtherExplanation}
                  required={this.props.required}
                />
              </Field>
            </>
          )}
        </Field>
        {!this.props.Dates?.present && (
          <>
            {/* PVQ: What type of discharge or separation did you receive? */}
            <Field
              adjustFor="big-buttons"
              attribute={setAttribute(attribute, 'DischargeType')}
              scrollIntoView={this.props.scrollIntoView}
              shrink
              title={i18n.t('military.pvq.history.heading.discharge.type')}
              titleSize="label"
            >
              <RadioGroup
                attribute={setAttribute(attribute, 'DischargeType')}
                className="discharge-type option-list option-list-vertical"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                required={this.props.required}
                selectedValue={(this.props.DischargeType || {}).value}
              >
                <Radio
                  className="discharge-type-honorable"
                  label={i18n.t('military.pvq.history.label.discharge.type.honorable')}
                  name="discharge-type-honorable"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateDischargeType}
                  value="Honorable"
                />
                <Radio
                  className="discharge-type-general"
                  label={i18n.t('military.pvq.history.label.discharge.type.general')}
                  name="discharge-type-general"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateDischargeType}
                  value="General"
                />
                <Radio
                  className="discharge-type-otherthan long-text"
                  label={i18n.t('military.pvq.history.label.discharge.type.otherthan')}
                  name="discharge-type-otherthan"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateDischargeType}
                  value="OtherThan"
                />
                <Radio
                  className="discharge-type-dismissal long-text"
                  label={i18n.t('military.pvq.history.label.discharge.type.dismissal')}
                  name="discharge-type-dismissal"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateDischargeType}
                  value="Dismissal"
                />
                <Radio
                  className="discharge-type-badconduct"
                  label={i18n.t('military.pvq.history.label.discharge.type.badconduct')}
                  name="discharge-type-badconduct"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateDischargeType}
                  value="BadConduct"
                />
                <Radio
                  className="discharge-type-dishonorable"
                  label={i18n.t('military.pvq.history.label.discharge.type.dishonorable')}
                  name="discharge-type-dishonorable"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateDischargeType}
                  value="Dishonorable"
                />
                <Radio
                  className="discharge-type-othertype long-text"
                  label={i18n.t('military.pvq.history.label.discharge.type.othertype')}
                  name="discharge-type-othertype"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateDischargeType}
                  value="OtherType"
                />
              </RadioGroup>
              {/* Present this question when anything other than honorable discharge is presended */}
              {this.props.DischargeType?.value !== 'Honorable' && this.props.DischargeType?.value !== '' && (
                <>
                  {/* PVQ: Why were you discharged or separated? */}
                  <Field
                    adjustFor="textarea"
                    attribute={setAttribute(attribute, 'DischargeReason')}
                    scrollIntoView={this.props.scrollIntoView}
                    title={i18n.t('military.pvq.history.label.discharge.reason')}
                  >
                    <Textarea
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...this.props.DischargeReason}
                      attribute={setAttribute(attribute, 'DischargeReason')}
                      className="discharge-reason"
                      name="DischargeReason"
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                      onError={this.props.onError}
                      onUpdate={this.updateDischargeReason}
                      required={this.props.required}
                    />
                  </Field>
                </>
              )}
            </Field>
          </>
        )}
        <DisciplinaryCollection
          {...this.props.List}
          attribute={setAttribute(attribute, 'List')}
          formType={this.props.formType}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          onError={this.props.onError}
          onUpdate={this.updateList}
          required={this.props.required}
        />
      </div>
    ) : (
      <div className="military-service">
        <Field
          title={i18n.t('military.history.heading.service')}
          adjustFor="big-buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Service`}
          exactAttribute
        >
          <RadioGroup
            className="service option-list eapp-extend-labels option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Service || {}).value}
            attribute={`${attribute}.Service`}
          >
            <Radio
              name="service-airforce"
              className="service-airforce"
              label={i18n.t('military.history.label.airforce')}
              value="AirForce"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-airnationalguard"
              className="service-airnationalguard"
              label={i18n.t('military.history.label.airnationalguard')}
              value="AirNationalGuard"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-army"
              className="service-army"
              label={i18n.t('military.history.label.army')}
              value="Army"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-armynationalguard"
              className="service-armynationalguard"
              label={i18n.t('military.history.label.armynationalguard')}
              value="ArmyNationalGuard"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-coastguard"
              className="service-coastguard"
              label={i18n.t('military.history.label.coastguard')}
              value="CoastGuard"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-marinecorps"
              className="service-marinecorps"
              label={i18n.t('military.history.label.marinecorps')}
              value="MarineCorps"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-navy"
              className="service-navy"
              label={i18n.t('military.history.label.navy')}
              value="Navy"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('military.history.heading.status')}
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Status`}
        >
          <RadioGroup
            className="status option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Status || {}).value}
            attribute={`${attribute}.Status`}
          >
            <Radio
              name="status-activeduty"
              className="status-activeduty"
              label={i18n.t('military.history.label.activeduty')}
              value="ActiveDuty"
              onUpdate={this.updateStatus}
              onError={this.props.onError}
            />
            <Radio
              name="status-activereserve"
              className="status-activereserve"
              label={i18n.t('military.history.label.activereserve')}
              value="ActiveReserve"
              onUpdate={this.updateStatus}
              onError={this.props.onError}
            />
            <Radio
              name="status-inactivereserve"
              className="status-inactivereserve"
              label={i18n.t('military.history.label.inactivereserve')}
              value="InactiveReserve"
              onUpdate={this.updateStatus}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('military.history.heading.officer')}
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Officer`}
        >
          <RadioGroup
            className="officer option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Officer || {}).value}
            attribute={`${attribute}.Officer`}
          >
            <Radio
              name="officer-officer"
              className="officer-officer"
              label={i18n.t('military.history.label.officer')}
              value="Officer"
              onUpdate={this.updateOfficer}
              onError={this.props.onError}
            />
            <Radio
              name="officer-enlisted"
              className="officer-enlisted"
              label={i18n.t('military.history.label.enlisted')}
              value="Enlisted"
              onUpdate={this.updateOfficer}
              onError={this.props.onError}
            />
            <Radio
              name="officer-na"
              className="officer-na"
              label={i18n.t('military.history.label.na')}
              value="NotApplicable"
              onUpdate={this.updateOfficer}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('military.history.heading.number')}
          help="military.history.help.number"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ServiceNumber`}
        >
          {i18n.m('military.selective.heading.message')}
          <Text
            name="ServiceNumber"
            {...this.props.ServiceNumber}
            className="service-number"
            onUpdate={this.updateServiceNumber}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.ServiceNumber`}
          />
        </Field>

        <Field
          title={i18n.t('military.history.heading.dates')}
          help="military.history.help.dates"
          adjustFor="daterange"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            className="dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Show
          when={
            (this.props.Service || {}).value
            && ((this.props.Service || {}).value === 'AirNationalGuard'
              || (this.props.Service || {}).value === 'ArmyNationalGuard')
          }
        >
          <Field
            title={i18n.t('military.history.heading.servicestate')}
            adjustFor="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ServiceState`}
          >
            <Location
              name="ServiceState"
              className="service-state"
              {...this.props.ServiceState}
              layout={Location.STATE}
              onUpdate={this.updateServiceState}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.ServiceState`}
            />
          </Field>
        </Show>

        <Branch
          name="has_beendischarged"
          label={i18n.t('military.history.heading.discharged')}
          labelSize="h4"
          className="discharged"
          {...this.props.HasBeenDischarged}
          onUpdate={this.updateDischarged}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HasBeenDischarged`}
        />
        <Comments
          title={i18n.t('military.history.servicecomments')}
          name="Comments"
          attribute="Comments"
          className="optional-comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          onError={this.props.onError}
          required={false}
        />
        <Show when={(this.props.HasBeenDischarged || {}).value === 'Yes'}>
          <div>
            <Field
              title={i18n.t('military.history.heading.details')}
              titleSize="h4"
              optional
              className="no-margin-bottom"
            />

            <Field
              title={i18n.t('military.history.heading.discharge.type')}
              titleSize="label"
              adjustFor="big-buttons"
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DischargeType`}
            >
              <RadioGroup
                className="discharge-type option-list option-list-vertical"
                required={this.props.required}
                onError={this.props.onError}
                selectedValue={(this.props.DischargeType || {}).value}
                attribute={`${attribute}.DischargeType`}
              >
                <Radio
                  name="discharge-type-honorable"
                  className="discharge-type-honorable"
                  label={i18n.t('military.history.label.discharge.type.honorable')}
                  value="Honorable"
                  onUpdate={this.updateDischargeType}
                  onError={this.props.onError}
                />
                <Radio
                  name="discharge-type-dishonorable"
                  className="discharge-type-dishonorable"
                  label={i18n.t('military.history.label.discharge.type.dishonorable')}
                  value="Dishonorable"
                  onUpdate={this.updateDischargeType}
                  onError={this.props.onError}
                />
                <Radio
                  name="discharge-type-lessthan"
                  className="discharge-type-lessthan long-text"
                  label={i18n.t('military.history.label.discharge.type.lessthan')}
                  value="LessThan"
                  onUpdate={this.updateDischargeType}
                  onError={this.props.onError}
                />
                <Radio
                  name="discharge-type-general"
                  className="discharge-type-general"
                  label={i18n.t('military.history.label.discharge.type.general')}
                  value="General"
                  onUpdate={this.updateDischargeType}
                  onError={this.props.onError}
                />
                <Radio
                  name="discharge-type-badconduct"
                  className="discharge-type-badconduct"
                  label={i18n.t('military.history.label.discharge.type.badconduct')}
                  value="BadConduct"
                  onUpdate={this.updateDischargeType}
                  onError={this.props.onError}
                />
                <Radio
                  name="discharge-type-other"
                  className="discharge-type-other long-text"
                  label={i18n.t('military.history.label.discharge.type.other')}
                  value="Other"
                  onUpdate={this.updateDischargeType}
                  onError={this.props.onError}
                />
              </RadioGroup>
              <Show when={(this.props.DischargeType || {}).value === 'Other'}>
                <Field
                  title={i18n.t('military.history.label.discharge.type.otherex')}
                  titleSize="label"
                  adjustFor="text"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.DischargeTypeOther`}
                >
                  <Text
                    name="DischargeTypeOther"
                    {...this.props.DischargeTypeOther}
                    className="discharge-type-otherex"
                    maxlength="100"
                    onUpdate={this.updateDischargeTypeOther}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.DischargeTypeOther`}
                  />
                </Field>
              </Show>
            </Field>

            <Show
              when={
                (this.props.DischargeType || {}).value
                && (this.props.DischargeType || {}).value !== 'Honorable'
              }
            >
              <Field
                title={i18n.t('military.history.label.discharge.reason')}
                adjustFor="textarea"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.DischargeReason`}
              >
                <Textarea
                  name="DischargeReason"
                  {...this.props.DischargeReason}
                  className="discharge-reason"
                  onUpdate={this.updateDischargeReason}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.DischargeReason`}
                />
              </Field>
              <Comments
                title={i18n.t('military.history.otherthanhonorablecomments')}
                name="OtherThanHonorableComments"
                attribute="OtherThanHonorableComments"
                className="other-than-honorable-comments"
                {...this.props.OtherThanHonorableComments}
                onUpdate={this.updateOtherThanHonorableComments}
                onError={this.props.onError}
                required={false}
              />
            </Show>
            <Field
              title={i18n.t('military.history.heading.discharge.date')}
              help="military.history.help.discharge.date"
              adjustFor="labels"
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DischargeDate`}
            >
              <DateControl
                name="DischargeDate"
                {...this.props.DischargeDate}
                className="discharge-date"
                minDateEqualTo
                hideDay
                maxDate={{ year: new Date().getFullYear() }}
                onUpdate={this.updateDischargeDate}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.DischargeDate`}
              />
            </Field>
            <Comments
              title={i18n.t('military.history.dischargecomments')}
              name="DischargeComments"
              attribute="DischargeComments"
              className="discharge-comments"
              {...this.props.DischargeComments}
              onUpdate={this.updateDischargeComments}
              onError={this.props.onError}
              required={false}
            />
          </div>
        </Show>
      </div>
    );
  }
}

MilitaryService.defaultProps = {
  List: {} as MilitaryHistoryItemModel['List'],
  Rank: {} as MilitaryHistoryItemModel['Rank'],
  Service: {} as MilitaryHistoryItemModel['Service'],
  Status: {} as MilitaryHistoryItemModel['Status'],
  StatusOtherExplanation: {} as MilitaryHistoryItemModel['StatusOtherExplanation'],
  Officer: {} as MilitaryHistoryItemModel['Officer'],
  ServiceNumber: {} as MilitaryHistoryItemModel['ServiceNumber'],
  Dates: {} as MilitaryHistoryItemModel['Dates'],
  ServiceState: {} as MilitaryHistoryItemModel['ServiceState'],
  DischargeType: {} as MilitaryHistoryItemModel['DischargeType'],
  DischargeTypeOther: {} as MilitaryHistoryItemModel['DischargeTypeOther'],
  DischargeReason: {} as MilitaryHistoryItemModel['DischargeReason'],
  DischargeDate: {} as MilitaryHistoryItemModel['DischargeDate'],
  HasBeenDischarged: {} as MilitaryHistoryItemModel['HasBeenDischarged'],
  Comments: {} as MilitaryHistoryItemModel['Comments'],
  DischargeComments: {} as MilitaryHistoryItemModel['DischargeComments'],
  OtherThanHonorableComments: {} as MilitaryHistoryItemModel['OtherThanHonorableComments'],
  Country: {} as MilitaryHistoryItemModel['Country'],
  CountryFocused: false,
  // non-Model properties below
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onUpdate: (_militaryHistoryItemModel: Partial<MilitaryHistoryItemModel>) => {},
};
